import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getOrganizationClientRecordInvoiceItemCacheKey,
    getOrganizationClientRecordInvoiceItemsCacheKey,
    onClientRecordInvoiceItemsUpdate,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('useOrganizationClientRecordInvoiceItems', {
    failedToLoadInvoiceItems: 'Failed to load invoice items',
    failedToLoadInvoiceItem: 'Failed to load invoice item',
    successfullyCreatedInvoiceItem: 'Successfully created invoice item',
    failedToCreateInvoiceItem: 'Failed to create invoice item',
    successfullyUpdatedInvoiceItem: 'Successfully updated invoice item',
    failedToUpdateInvoiceItem: 'Failed to update invoice item',
    successfullyDeletedInvoiceItem: 'Successfully deleted invoice item',
    failedToDeleteInvoiceItem: 'Failed to delete invoice item',
    successfullyDeletedInvoiceItems: 'Successfully deleted {count} invoice item{plural}',
    failedToDeletedInvoiceItems: 'Failed to delete {count} invoice item{plural}',
});

export type UseOrganizationClientRecordInvoiceItemsArgs = {
    clientRecordId: string;
    invoiceItemId?: string;
    onUpdatedInvoiceItem?: () => void;
    onDeletedInvoiceItem?: () => void;
    onCreateInvoiceItem?: () => void;
};

export type UpdateInvoiceItemArgs = {
    invoiceItemId: string;
    invoiceItem: APITypes.organizationService.UpdateOrganizationClientRecordInvoiceItemRequest;
};

export type UseOrganizationClientRecordInvoiceItemsReturnValue = {
    invoiceItems: APITypes.organizationService.ListOrganizationClientRecordInvoiceItemsResponse | null;
    isLoadingInvoiceItems: boolean;
    invoiceItem: APITypes.organizationService.GetOrganizationClientRecordInvoiceItemResponse | null;
    isLoadingInvoiceItem: boolean;
    createInvoiceItem: (
        invoiceItem: APITypes.organizationService.CreateOrganizationClientRecordInvoiceItemsRequest,
    ) => void;
    isCreatingInvoiceItem: boolean;
    updateInvoiceItem: (args: UpdateInvoiceItemArgs) => void;
    isUpdatingInvoiceItem: boolean;
    deleteInvoiceItem: (invoiceItemId: string) => void;
    isDeletingInvoiceItem: boolean;
    deleteInvoiceItems: (invoiceItemIds: string[]) => void;
    isDeletingInvoiceItems: boolean;
};

export default function useOrganizationClientRecordInvoiceItems({
    clientRecordId,
    invoiceItemId,
    onUpdatedInvoiceItem,
    onDeletedInvoiceItem,
    onCreateInvoiceItem,
}: UseOrganizationClientRecordInvoiceItemsArgs): UseOrganizationClientRecordInvoiceItemsReturnValue {
    const organizationId = useOrganizationId();
    const notify = useNotify();
    const invoiceItemsQuery = useQuery({
        queryKey: getOrganizationClientRecordInvoiceItemsCacheKey(clientRecordId),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.ListOrganizationClientRecordInvoiceItemsResponse>(
                urls.api.organizations.clientRecords.invoiceItems(organizationId, clientRecordId),
            );
        },
    });
    useQueryErrorNotify(invoiceItemsQuery, intl('failedToLoadInvoiceItems'));

    const hasInvoiceItemId = !!invoiceItemId && invoiceItemId !== 'undefined' && invoiceItemId !== 'null';
    const invoiceItemQuery = useQuery({
        queryKey: getOrganizationClientRecordInvoiceItemCacheKey(clientRecordId, invoiceItemId!),
        queryFn: async () => {
            if (!hasInvoiceItemId) return null;
            return await appAPI.get<APITypes.organizationService.GetOrganizationClientRecordInvoiceItemResponse>(
                urls.api.organizations.clientRecords.invoiceItem(organizationId, clientRecordId, invoiceItemId!),
            );
        },
        enabled: hasInvoiceItemId,
    });
    useQueryErrorNotify(invoiceItemQuery, intl('failedToLoadInvoiceItem'));

    const createInvoiceItemMutation = useMutation({
        mutationFn: async (
            invoiceItem: APITypes.organizationService.CreateOrganizationClientRecordInvoiceItemsRequest,
        ) => {
            return await appAPI.post(
                urls.api.organizations.clientRecords.bulkAddInvoiceItems(organizationId, clientRecordId),
                {
                    body: invoiceItem,
                },
            );
        },
        onSuccess: () => {
            notify('success', intl('successfullyCreatedInvoiceItem'));
            onCreateInvoiceItem?.();
            onClientRecordInvoiceItemsUpdate.resetCache({ organizationClientRecordId: clientRecordId })();
        },
        onError: () => {
            notify('error', intl('failedToCreateInvoiceItem'));
        },
    });

    const updateInvoiceItemMutation = useMutation({
        mutationFn: async ({ invoiceItemId, invoiceItem }: UpdateInvoiceItemArgs) => {
            return await appAPI.put(
                urls.api.organizations.clientRecords.invoiceItem(organizationId, clientRecordId, invoiceItemId),
                {
                    body: invoiceItem,
                },
            );
        },
        onSuccess: () => {
            notify('success', intl('successfullyUpdatedInvoiceItem'));
            onUpdatedInvoiceItem?.();
            onClientRecordInvoiceItemsUpdate.resetCache({ organizationClientRecordId: clientRecordId })();
        },
        onError: () => {
            notify('error', intl('failedToUpdateInvoiceItem'));
        },
    });

    const deleteInvoiceItemMutation = useMutation({
        mutationFn: async (invoiceItemId: string) => {
            return await appAPI.del(
                urls.api.organizations.clientRecords.invoiceItem(organizationId, clientRecordId, invoiceItemId),
            );
        },
        onSuccess: () => {
            notify('success', intl('successfullyDeletedInvoiceItem'));
            onDeletedInvoiceItem?.();
            onClientRecordInvoiceItemsUpdate.resetCache({ organizationClientRecordId: clientRecordId })();
        },
        onError: () => {
            notify('error', intl('failedToDeleteInvoiceItem'));
        },
    });

    const deleteInvoiceItemsMutation = useMutation({
        mutationFn: async (invoiceItemIds: string[]) => {
            await Promise.all(
                invoiceItemIds.map((_) =>
                    appAPI.del(urls.api.organizations.clientRecords.invoiceItem(organizationId, clientRecordId, _)),
                ),
            );
        },
        onSuccess: (_data, ids) => {
            notify(
                'success',
                intl('successfullyDeletedInvoiceItems', { count: ids.length, plural: ids.length > 1 ? 's' : '' }),
            );
            onDeletedInvoiceItem?.();
            onClientRecordInvoiceItemsUpdate.resetCache({ organizationClientRecordId: clientRecordId })();
        },
        onError: (_data, ids) => {
            notify(
                'error',
                intl('failedToDeletedInvoiceItems', { count: ids.length, plural: ids.length > 1 ? 's' : '' }),
            );
        },
    });

    return {
        invoiceItems: invoiceItemsQuery.data || null,
        isLoadingInvoiceItems: invoiceItemsQuery.isLoading,
        invoiceItem: invoiceItemQuery.data || null,
        isLoadingInvoiceItem: invoiceItemQuery.isLoading,
        createInvoiceItem: createInvoiceItemMutation.mutate,
        isCreatingInvoiceItem: createInvoiceItemMutation.isPending,
        updateInvoiceItem: updateInvoiceItemMutation.mutate,
        isUpdatingInvoiceItem: updateInvoiceItemMutation.isPending,
        deleteInvoiceItem: deleteInvoiceItemMutation.mutate,
        isDeletingInvoiceItem: deleteInvoiceItemMutation.isPending,
        deleteInvoiceItems: deleteInvoiceItemsMutation.mutate,
        isDeletingInvoiceItems: deleteInvoiceItemsMutation.isPending,
    };
}
